import React, { useState, useEffect, useRef } from "react"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/airbnb.css"
import { post, get } from "../utils/apiClient"
import { Loader, downloadPdfFromBase64 } from "./../utils/common"
import Pagination from "./common/pagination"
import FaxView from "./faxView"
import Select from "react-select"
import toast from "react-hot-toast"
import moment from "moment"

const statusOptions = [
  {
    value: "ALL",
    label: "ALL",
  },
  {
    value: "ERROR",
    label: "ERROR",
  },
  {
    value: "COMPLETE",
    label: "COMPLETE",
  },
]

const customSelectStyles = {
  container: (base) => ({
    ...base,
    width: "auto",
    maxWidth: 400,
    marginRight: "5px",
  }),
  control: (base) => ({
    ...base,
    borderRadius: 0,
    minWidth: 120,
    minHeight: 31,
    height: 31,
    display: "flex",
    alignItems: "center",
  }),
  valueContainer: (base) => ({
    ...base,
    height: 31,
    padding: "0 8px",
    display: "flex",
    alignItems: "center",
  }),
  placeholder: (base) => ({
    ...base,
    margin: 0,
    lineHeight: "31px",
    whiteSpace: "nowrap",
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    lineHeight: "31px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),

  indicatorsContainer: (base) => ({
    ...base,
    height: 31,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 4,
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: 4,
  }),
  menu: (base) => ({
    ...base,
    width: "max-content",
    maxWidth: 400,
  }),
  menuList: (base) => ({
    ...base,
    maxWidth: 400,
    overflowX: "auto",
  }),
}

export default function AuditSearch({ authUser }) {
  const [dateFrom, setDateFrom] = useState(null)
  const [dateTo, setDateTo] = useState(null)
  const [status, setStatus] = useState({
    value: "ALL",
    label: "ALL",
  })
  const [faxes, setFaxes] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedTime, setSelectedTime] = useState(null)
  const [loadingDownload, setLoadingDownload] = useState(null)
  const [faxView, setFaxView] = useState(null)

  // Search Filter
  const filteredFaxes = faxes?.filter((fax) => {
    if (!selectedTime) return true

    return fax.addressBookTimeCombined === selectedTime.value
  })

  const pageSize = 20
  const startIdx = (currentPage - 1) * pageSize
  const currentFaxes = filteredFaxes.slice(startIdx, startIdx + pageSize)

  const fetchFaxes = async () => {
    // if (!dateFrom || !dateTo) {
    //   toast.error("Please select Date Range (From/To)")
    //   return
    // }

    if (dateFrom && dateTo && dateFrom >= dateTo) {
      toast.error("From date must be earlier than To date")
      return
    }

    setLoading(true)
    try {
      const minTS = dateFrom.toISOString().slice(0, 19)
      const maxTS = dateTo.toISOString().slice(0, 19)
      const payload = { minTS, maxTS, transmissionStatus: status.value }
      const res = await post("/getOutboundFaxes", payload, {
        ref: authUser?.faxNumber,
      })

      const finalFaxes = res.faxes.map((item) => {
        const { folder, date } = splitCode(
          item?.client_tracking_data?.client_code,
        )

        const formattedDate = date
          ? moment.unix(date).format("MM/DD/YYYY hh:mm A")
          : ""

        return {
          ...item,
          addressBook: folder,
          addressBookTime: date,
          addressBookTimeFormatted: formattedDate,
          addressBookTimeCombined: folder
            ? `${folder} | ${formattedDate}`
            : null,
        }
      })

      setFaxes(finalFaxes || [])
      setCurrentPage(1) // reset to first page
    } catch (err) {
      console.error("Failed to fetch faxes:", err)
      toast.error("Failed to fetch faxes")
    } finally {
      setLoading(false)
    }
  }

  const resetAll = () => {
    setDateFrom(null)
    setDateTo(null)
    setSelectedTime(null)
    setFaxes([])
    setCurrentPage(1)
    setStatus({
      value: "ALL",
      label: "ALL",
    })
  }

  const handleExportCsv = () => {
    if (currentFaxes.length === 0) {
      toast.error("No data to export")
      return
    }
    // CSV header
    const headers = [
      "Completed Timestamp",
      "Fax Number",
      "Pages",
      "Status",
      "Error Message",
      "Address Book",
      "Sent Time",
    ]
    // Build rows
    const rows = filteredFaxes.map((fax) => [
      moment(fax.completed_timestamp).format("MM/DD/YYYY hh:mm A"),
      fax.destination_fax_number,
      fax.pages,
      fax?.transmission_data?.transmission_status,
      fax.transmission_data?.error_message || "",
      fax?.addressBook,
      fax?.addressBookTimeFormatted,
    ])
    // Assemble CSV string
    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map(
            (cell) =>
              // escape quotes
              `"${String(cell).replace(/"/g, '""')}"`,
          )
          .join(","),
      )
      .join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "faxes.csv"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  function splitCode(value) {
    if (!value || !value.includes("|")) {
      return { folder: value, date: null }
    }

    const [folder, date] = value.split("|")
    return { folder, date }
  }

  const downloadPdf = async (fax) => {
    try {
      setLoadingDownload(fax.fax_id)

      const data = await get(
        `/fax-image?faxId=${fax.fax_id}&ref=${authUser?.faxNumber}`,
      )

      if (data?.image) {
        downloadPdfFromBase64(data?.image, `${fax.fax_id}.pdf`)
      }

      setLoadingDownload(false)
    } catch (err) {
      toast.error(err, {
        duration: 5000,
      })
      setLoadingDownload(false)
    }
  }

  useEffect(() => {
    if (dateFrom && dateTo) fetchFaxes()
  }, [status, dateTo, dateFrom])

  const timeOptions = [
    ...new Set(faxes.map((r) => r.addressBookTimeCombined).filter(Boolean)),
  ]
    .map((q) => ({
      value: q,
      label: q,
    }))
    .sort((a, b) => a.value.localeCompare(b.value))

  // Adjust to your actual header height
  const HEADER_HEIGHT = 64

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
      }}
    >
      {loading && (
        <div className="loading-overlay">
          <Loader />
        </div>
      )}

      {/* --- Search / Filter Bar --- */}
      <div
        className="audit-search-container"
        style={{
          flexShrink: 0,
          display: "flex",
          gap: "0px",
          justifyContent: "space-between",
        }}
      >
        <div style={{ flexShrink: 0, display: "flex", gap: "0px" }}>
          {/* <span style={{ marginRight: "2px" }}>From:</span> */}
          <Flatpickr
            value={dateFrom}
            onChange={([d]) => setDateFrom(d)}
            options={{
              enableTime: true,
              dateFormat: "Y-m-d H:i",
              altInput: false,
              altFormat: "Y-m-d h:i K",
              minuteIncrement: 15,
              appendTo: document.body,
              position: "auto center",
            }}
            placeholder="From Date"
            className="flatpickr-input"
          />
          {/* <span style={{ marginRight: "2px" }}>To:</span> */}
          <Flatpickr
            value={dateTo}
            onChange={([d]) => setDateTo(d)}
            options={{
              enableTime: true,
              dateFormat: "Y-m-d H:i",
              altInput: false,
              altFormat: "Y-m-d h:i K",
              minuteIncrement: 15,
              appendTo: document.body,
              position: "auto center",
            }}
            placeholder="To Date"
            className="flatpickr-input"
          />
          {/* <span style={{ marginRight: "2px" }}>Status:</span> */}

          <Select
            className="basic-single"
            placeholder="Filter by Status"
            options={statusOptions}
            value={status}
            onChange={(v) => {
              setStatus(v)
              setCurrentPage(1)
            }}
            // isClearable
            styles={customSelectStyles}
          />

          <Select
            className="basic-single"
            placeholder="Filter by Time"
            options={timeOptions}
            value={selectedTime}
            onChange={(v) => {
              setSelectedTime(v)
              setCurrentPage(1)
            }}
            isClearable
            styles={customSelectStyles}
          />

          <span
            onClick={resetAll}
            title="Reset Filters"
            style={{ cursor: "pointer", marginTop: "5px", marginLeft: "5px" }}
          >
            ↺
          </span>
        </div>
        <div>
          {/* <span style={{ marginLeft: "5px", color: "#ccc" }}>|</span> */}
          {/* <button
          className="faxes-button"
          onClick={fetchFaxes}
          style={{ marginLeft: "5px" }}
        >
          Search
        </button> */}

          {/* <span style={{ marginLeft: "5px", color: "#ccc" }}>|</span> */}
          <button
            className="faxes-button"
            onClick={handleExportCsv}
            style={{ marginLeft: "5px" }}
          >
            Export to CSV
          </button>
        </div>
      </div>

      {/* --- Table + Pagination --- */}
      <div
        className="faxes-content"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div
          className="table-container"
          style={{ flex: 1, overflowY: "auto", position: "relative" }}
        >
          <table className="faxes-table" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {[
                  "Date Time",
                  "Fax Number",
                  "Pages",
                  "Status",
                  "Message",
                  "Address Book",
                  "Sent Time",
                  "Actions",
                ].map((label) => (
                  <th
                    key={label}
                    className="faxes-th"
                    style={{
                      position: "sticky",
                      top: 0,
                      background: "#fff",
                      zIndex: 0,
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentFaxes.length > 0 ? (
                currentFaxes.map((fax) => {
                  return (
                    <tr key={fax.fax_id} className="fax-item">
                      {/* <td className="faxes-td">{fax.fax_id}</td> */}
                      <td className="faxes-td">
                        {moment(fax.completed_timestamp).format(
                          "MM/DD/YYYY hh:mm A",
                        )}
                      </td>
                      <td className="faxes-td">{fax.destination_fax_number}</td>
                      <td className="faxes-td">{fax.pages}</td>
                      <td className="faxes-td">
                        {fax?.transmission_data?.transmission_status}
                      </td>
                      <td className="faxes-td">
                        {fax?.transmission_data?.error_message || "-"}
                      </td>
                      <td className="faxes-td">{fax?.addressBook}</td>
                      <td className="faxes-td">
                        {fax?.addressBookTimeFormatted}
                      </td>
                      <td className="faxes-td">
                        <span
                          style={{ marginRight: "5px", cursor: "pointer" }}
                          title="View"
                          onClick={(e) => {
                            e.stopPropagation()
                            setFaxView(fax)
                          }}
                        >
                          📄
                        </span>
                        <span
                          style={{ cursor: "pointer", fontSize: "14px" }}
                          title="Download"
                          onClick={(e) => {
                            e.stopPropagation()
                            downloadPdf(fax)
                          }}
                        >
                          {loadingDownload === fax.fax_id ? <>🔄</> : <>⬇️</>}
                        </span>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    style={{
                      textAlign: "center",
                      padding: "10px",
                      border: "1px solid #ccc",
                    }}
                  >
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {faxView && (
          <PdfView
            fax={faxView}
            onClose={() => {
              setFaxView(null)
            }}
            authUser={authUser}
          />
        )}

        {faxes.length > 0 && (
          <div
            className="faxes-pagination"
            style={{
              marginTop: "8px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {faxes.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredFaxes.length / pageSize)}
                onPageChange={(p) => setCurrentPage(p)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const PdfView = ({ fax, authUser, onClose }) => {
  const [loading, setLoading] = useState(false)
  const [document, setDocument] = useState(null)
  const fetched = useRef(false)

  const fetchFax = async (faxId) => {
    try {
      setLoading(true)

      const data = await get(
        `/fax-image?faxId=${fax.fax_id}&ref=${authUser?.faxNumber}`,
      )

      if (data) {
        setDocument(data)
      } else {
        setDocument(null)
      }

      setLoading(false)
    } catch (err) {
      setLoading(false)
      setDocument(null)
    }
  }

  useEffect(() => {
    if (fax?.fax_id && !fetched.current) {
      fetched.current = true
      fetchFax(fax?.fax_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fax?.fax_id])

  return (
    <div className="popup-overlay">
      <div className="view-popup-modal">
        <div className="checkbox-popup-header">
          <h3 className="popup-title" style={{ marginTop: "0px" }}>
            Fax: {fax?.destination_fax_number}
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
        <div className="fax-view">
          <div className="fax-view-image">
            <FaxView
              base64String={document?.image}
              selectedFax={fax}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
